import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import SearchInput from '@components/atoms/search-input/search-input';
import { CatalogFilterConfiguration, CatalogFilterConfigurationType } from '@interfaces/models/catalogFilter';
import { ElasticSearchFacet } from '@interfaces/api/responses/elasticSearch';
import { ElasticSearchFacetsFields } from '@interfaces/models/elasticSearch';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { facetToFilterRename } from '@maps/catalog/search-product-facets-filters-maps';
import { booleanFilters } from '@constants/catalog';
import useActiveDealQuery from '@hooks/deal/use-active-deal-query';
import useWindowSize from '@hooks/use-window-size';
import useMarketingFiltersQuery from '@hooks/marketing-filters/use-marketing-filters-query';
import { useFilterListKeyboardNavigation } from '@hooks/a11y/use-filter-list-keyboard-navigation';
import GenericItem from './components/GenericItem';
import PriceRangeItem from './components/PriceRangeItem';
import styles from './filters.module.scss';

type FiltersListProps = {
  filterGroup: ElasticSearchFacetsFields;
  variant: CatalogFilterConfigurationType;
  searchable?: boolean;
  facets: ElasticSearchFacet[];
  filterConfiguration?: CatalogFilterConfiguration;
  searchTerm?: string;
  setSearchTerm?: React.Dispatch<React.SetStateAction<string>>;
  scrollLimit?: number;
};

interface ElasticSearchFacetExtension extends ElasticSearchFacet {
  filterIds: string[];
}

const FilterList: React.FC<FiltersListProps> = (props) => {
  const {
    filterGroup,
    variant = 'item',
    searchable = variant === 'item',
    facets,
    filterConfiguration,
    searchTerm,
    setSearchTerm,
    scrollLimit = 8,
  } = props;

  const onChangeHandler = useContextSelector(CatalogContext, (v) => v.toggleFilter);
  const staticFilterGroups = useContextSelector(CatalogContext, (v) => v.staticFilterGroups);

  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { activeDeal } = useActiveDealQuery();
  const filters = useContextSelector(CatalogContext, (v) => v.filters);
  const updatePriceRangeFilter = useContextSelector(CatalogContext, (v) => v.updatePriceRangeFilter);
  const { isWiderThanMd } = useWindowSize();
  const [scrolled, setScrolled] = useState(false);
  const { freeShippingFilter } = useMarketingFiltersQuery();

  const searchEl = useRef(null);
  const listRef = useRef<HTMLUListElement>(null);

  // TODO: Type this
  const selectedValues = useMemo(() => {
    const filterGroupName = facetToFilterRename(filterGroup);

    if (!filters) {
      return [];
    }
    // For boolean properties, we just check if they exist
    // TODO: Fix types
    // @ts-ignore
    if (booleanFilters.includes(filterGroupName)) {
      return filters.hasOwnProperty(filterGroupName);
    }
    return filters[filterGroupName] ?? [];
  }, [filters]);

  const customStyling = {
    'color-swatch': styles.filtersContainer_colorSwatch,
    'text-swatch': styles.filtersContainer_textSwatch,
    item: styles.filtersContainer_item,
  };

  const handleSearchReset = (): void => {
    setSearchTerm('');
  };

  const handleSearch = (e): void => {
    setSearchTerm(e);
  };

  // Some labels require special rules. Index is used to determine which key to use in predefined itemText array
  const getItemLabel = (
    item: ElasticSearchFacet,
    filterGroupVariant: CatalogFilterConfigurationType,
    index: number,
  ): string => {
    if (filterGroup === 'dealEligible') {
      return `${t(filterConfiguration?.itemText[index], {
        dealsName: activeDeal?.localizedName,
      })}`;
    }
    if (filterGroup === 'localCountries' && !!freeShippingFilter) {
      return freeShippingFilter.title;
    }
    const hasPredefinedTranslationKey: boolean =
      !!filterConfiguration?.itemText && !!filterConfiguration?.itemText[index];
    return hasPredefinedTranslationKey ? t(filterConfiguration.itemText[index]) : item.name;
  };

  const computedFacets = useMemo(() => {
    if (['categoryLvl0', 'categoryLvl1', 'categoryLvl2'].includes(filterGroup)) {
      return facets.reduce((currentFacetList, currentFacet) => {
        const facet = currentFacetList.find((i) => i.name.trim() === currentFacet.name.trim());
        if (facet) {
          facet.filterIds = [...facet.filterIds, currentFacet.id];
          facet.count = facet.count + currentFacet.count;
          return currentFacetList;
        }

        const extendedFacet = { ...currentFacet, filterIds: [currentFacet.id] };
        return [...currentFacetList, extendedFacet];
      }, [] as ElasticSearchFacetExtension[]);
    }

    return facets.map((facet) => ({ ...facet, filterIds: [facet.id] }));
  }, [facets]);

  const isFilterDisabled = !!staticFilterGroups[filterGroup];

  const doLazyLoadOnScroll = filterConfiguration?.lazyLoadCount && isWiderThanMd;

  const facetsToDisplay = useMemo(() => {
    if (!doLazyLoadOnScroll || scrolled) {
      return computedFacets;
    }
    return computedFacets.slice(0, filterConfiguration.lazyLoadCount);
  }, [doLazyLoadOnScroll, computedFacets, filterConfiguration, scrolled]);

  const { handleKeyDown: handleListKeyDown } = useFilterListKeyboardNavigation({
    listRef,
    disabled: isFilterDisabled,
  });

  const handleScroll = () => {
    if (!doLazyLoadOnScroll) {
      return;
    }
    setScrolled(true);
  };

  return (
    <>
      {variant === 'price-range' && <PriceRangeItem onSubmit={(min, max) => updatePriceRangeFilter(min, max)} />}
      {variant !== 'price-range' && variant !== 'top-level-group' && (
        <>
          {searchable && setSearchTerm && !isFilterDisabled && (
            <SearchInput
              value={searchTerm}
              ref={searchEl}
              placeholder={t('CATALOG.FILTERS.SEARCH_PLACEHOLDER.DEFAULT')}
              onChange={handleSearch}
              onReset={handleSearchReset}
              filterName={filterGroup}
              identifier={'filter-search-' + filterGroup}
            />
          )}
          {facets && (
            <>
              {/* Screen reader announcement when the search results changes */}
              <span
                role="status"
                aria-atomic="true"
                aria-live="polite"
                className="visuallyhidden"
                aria-label={
                  facets?.length > 1
                    ? tA11y('A11Y.GENERIC_TOTAL_ITEMS', { totalItems: facets?.length })
                    : tA11y('A11Y.GENERIC_SINGLE_ITEM', { totalItems: facets?.length })
                }
              ></span>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onKeyDown delegates keyboard events bubbling from interactive children for roving tabindex navigation */}
              <ul
                ref={listRef}
                role="group"
                aria-label={filterConfiguration?.label ? t(filterConfiguration.label) : filterGroup}
                className={clsx(styles.filtersContainer, customStyling[variant], {
                  [styles.filtersContainer_scroll]:
                    facets.length > scrollLimit &&
                    !filterConfiguration?.showMoreLimit &&
                    filterConfiguration?.type === 'item',
                  [styles.filtersContainer_searcheable]: searchable && !isFilterDisabled,
                  [styles.filtersContainer_mobileItems]: variant === 'item',
                })}
                data-cy={'filter-' + filterGroup}
                onKeyDown={handleListKeyDown}
                onScroll={handleScroll}
                onFocus={handleScroll}
              >
                {facetsToDisplay.map((item, index: number) => {
                  const checked = Array.isArray(selectedValues) ? selectedValues.includes(item.id) : !!selectedValues;
                  return (
                    <li
                      key={`item_${item.id}`}
                      className={styles.filtersContainer__item}
                    >
                      <GenericItem
                        filterGroup={filterGroup}
                        id={item.id}
                        label={getItemLabel(item, variant, index)}
                        count={item.count}
                        variant={variant}
                        facet={item}
                        onChange={() => onChangeHandler(filterGroup, item.filterIds, item.name)}
                        checked={checked}
                        disabled={isFilterDisabled}
                      />

                      {checked && filterConfiguration?.child && !!item?.children?.length && (
                        <div className={styles.filtersContainer_nestedFilterItems}>
                          <FilterList
                            filterGroup={filterConfiguration.child.facet as ElasticSearchFacetsFields}
                            variant={filterConfiguration.child.type}
                            facets={item?.children}
                            searchable={false}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </>
      )}
    </>
  );
};

export default FilterList;
